import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  getConsent, saveConsentLocal, pushConsentMode, clearOptionalStorage, CONSENT_COOKIE,
} from "@/lib/tracking";

const TEXT_VERSION = "v1.0";

export default function ConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [stats, setStats] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const isAdminArea = typeof window !== "undefined" && window.location.pathname.startsWith("/admin");

  useEffect(() => {
    if (isAdminArea) return;
    const existing = getConsent();
    if (existing) {
      pushConsentMode(existing);
    } else {
      // Erst nach dem ersten Rendern einblenden (LCP nicht beeinflussen)
      const t = setTimeout(() => setVisible(true), 400);
      return () => clearTimeout(t);
    }
    const open = () => {
      const c = getConsent();
      setStats(!!c?.statistics);
      setMarketing(!!c?.marketing);
      setShowSettings(true);
      setVisible(true);
    };
    window.addEventListener("open-consent-settings", open);
    return () => window.removeEventListener("open-consent-settings", open);
  }, []);

  const persist = async (consent, action) => {
    saveConsentLocal(consent);
    pushConsentMode(consent);
    if (action === "revoked" || (!consent.statistics && !consent.marketing)) {
      clearOptionalStorage();
    }
    let consentId = localStorage.getItem("mabuhay_consent_id");
    if (!consentId) {
      consentId = `cons_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      localStorage.setItem("mabuhay_consent_id", consentId);
    }
    try {
      await api.post("/consent", {
        consent_id: consentId,
        necessary: true,
        statistics: consent.statistics,
        marketing: consent.marketing,
        text_version: TEXT_VERSION,
        action,
      });
    } catch { /* Consent-Funktion bleibt lokal wirksam */ }
    setVisible(false);
    setShowSettings(false);
  };

  if (!visible || isAdminArea) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[70] p-3 sm:p-4" role="dialog" aria-modal="true" aria-label="Cookie-Einwilligung" data-testid="consent-banner">
      <div className="max-w-3xl mx-auto bg-[#17161c] border border-[#32303c] rounded-lg shadow-2xl p-5 sm:p-6 max-h-[80vh] overflow-y-auto">
        <h2 className="text-base font-bold uppercase font-display tracking-wide mb-2">Privatsphäre-Einstellungen</h2>
        <p className="text-sm text-[#a6a29d] leading-relaxed mb-4">
          Wir nutzen notwendige Cookies für den Betrieb der Website. Optionale Dienste für Statistik und Marketing
          (z. B. Reichweitenmessung und Werbeaussteuerung) setzen wir nur mit Ihrer Einwilligung ein.
          Details finden Sie in der <a href="/datenschutz" className="underline text-[#d97724]">Datenschutzerklärung</a> (Textversion {TEXT_VERSION}).
        </p>

        {showSettings && (
          <div className="mb-4 space-y-3 border border-[#32303c] rounded p-4" data-testid="consent-settings-panel">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold">Notwendig</p>
                <p className="text-xs text-[#8f8a85]">Technisch erforderlich: Betrieb der Website, Anfrageformular,
                Ihre Einwilligungsauswahl selbst (Cookie/localStorage). Keine Analyse, keine Werbung.</p>
              </div>
              <span className="text-xs text-[#8f8a85] pt-1">immer aktiv</span>
            </div>
            <label className="flex items-start justify-between gap-4 cursor-pointer">
              <div>
                <p className="text-sm font-semibold">Statistik</p>
                <p className="text-xs text-[#8f8a85]">Anonyme Reichweitenmessung. Vorbereitet für Google Analytics 4 –
                aktuell ist kein Messdienst aktiv; es werden auch nach Zustimmung erst Requests gesendet, sobald der
                Betreiber eine Mess-ID konfiguriert. Keine Werbung.</p>
              </div>
              <input type="checkbox" className="mt-1 w-5 h-5 accent-[#d97724]" checked={stats}
                data-testid="consent-statistics-toggle" onChange={(e) => setStats(e.target.checked)} />
            </label>
            <label className="flex items-start justify-between gap-4 cursor-pointer">
              <div>
                <p className="text-sm font-semibold">Marketing</p>
                <p className="text-xs text-[#8f8a85]">Werbe-Messung und Herkunftszuordnung: Google Ads
                Conversion-Tracking (aktiv nach Einwilligung, inkl. gehashter Enhanced Conversions bei Anfragen).
                Meta Pixel/Conversions API vorbereitet, derzeit deaktiviert. Unabhängig von der Statistik-Einwilligung.</p>
              </div>
              <input type="checkbox" className="mt-1 w-5 h-5 accent-[#d97724]" checked={marketing}
                data-testid="consent-marketing-toggle" onChange={(e) => setMarketing(e.target.checked)} />
            </label>
            <p className="text-[11px] text-[#9a958f] leading-relaxed">
              Speicherfristen: Ihre Auswahl bleibt bis zum Widerruf bzw. bis zum Löschen der Browserdaten gespeichert;
              der serverseitige Einwilligungsnachweis (Auswahl, Zeitpunkt, Textversion – ohne Anfrageinhalte) wird nach
              12 Monaten automatisch gelöscht.
            </p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-2.5">
          {showSettings ? (
            <>
              <button type="button" data-testid="consent-save-button"
                onClick={() => persist({ necessary: true, statistics: stats, marketing }, "custom")}
                className="flex-1 px-4 py-3 min-h-[48px] bg-[#b35c0f] text-white text-xs font-bold uppercase tracking-wider rounded hover:bg-[#96500c] transition-colors">
                Auswahl speichern
              </button>
              <button type="button" data-testid="consent-revoke-button"
                onClick={() => persist({ necessary: true, statistics: false, marketing: false }, "revoked")}
                className="flex-1 px-4 py-3 min-h-[48px] border border-[#32303c] text-[#a6a29d] text-xs font-bold uppercase tracking-wider rounded hover:border-[#7e7a75] transition-colors">
                Widerrufen (nur notwendige)
              </button>
            </>
          ) : (
            <>
              <button type="button" data-testid="consent-accept-all-button"
                onClick={() => persist({ necessary: true, statistics: true, marketing: true }, "accept_all")}
                className="flex-1 px-4 py-3 min-h-[48px] bg-[#b35c0f] text-white text-xs font-bold uppercase tracking-wider rounded hover:bg-[#96500c] transition-colors">
                Alle akzeptieren
              </button>
              <button type="button" data-testid="consent-only-necessary-button"
                onClick={() => persist({ necessary: true, statistics: false, marketing: false }, "only_necessary")}
                className="flex-1 px-4 py-3 min-h-[48px] border border-[#32303c] text-[#f4efe8] text-xs font-bold uppercase tracking-wider rounded hover:border-[#7e7a75] transition-colors">
                Nur notwendige
              </button>
              <button type="button" data-testid="consent-open-settings-button"
                onClick={() => setShowSettings(true)}
                className="flex-1 px-4 py-3 min-h-[48px] border border-[#32303c] text-[#a6a29d] text-xs font-bold uppercase tracking-wider rounded hover:border-[#7e7a75] transition-colors">
                Einstellungen
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export { CONSENT_COOKIE };
